import { useNavigate } from 'react-router-dom';
import IconSvg from 'src/assets/icons/IconSvg';
import type { Raffle } from 'src/types/raffle';
import CancelRaffleBtn from './CancelRaffleBtn';

interface Props {
  raffle: Raffle;
  userId: string;
  onCancelled: () => void;
}

const satsToBtc = (sats: number) => (sats / 1e8).toFixed(4);

const statusConfig: Record<string, { label: string; color: string; icon?: string }> = {
  DRAFT: { label: 'Pending', color: 'text-system-error', icon: 'clockM' },
  LIVE: { label: 'Live', color: 'text-system-success', icon: 'liveS' },
  ENDED: { label: 'Ended', color: 'text-system-error' },
  COMPLETED: { label: 'Completed', color: 'text-system-success' },
  CANCELLED: { label: 'Cancelled', color: 'text-grayscale-400' },
};

const CreatedRaffleCard = ({ raffle, userId, onCancelled }: Props) => {
  const navigate = useNavigate();
  const status = statusConfig[raffle.status] ?? statusConfig.DRAFT;
  const endDate = new Date(raffle.endDate);
  const formattedEndDate = `${endDate.getDate().toString().padStart(2, '0')}/${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getFullYear()}`;
  const ticketPriceBtc = satsToBtc(raffle.ticketPriceSats);
  const totalValueBtc = satsToBtc(raffle.totalTicketValue);
  const imageUrl = raffle.ordinalImageUrl || raffle.runesImageUrl || '';

  return (
    <div className="space-y-2 w-full cursor-pointer" onClick={() => navigate(`/raffle/${raffle.id}`)}>
      <div className="h-[408px] rounded-[6px] relative overflow-hidden bg-grayscale-800">
        <div className={`absolute bg-brand-black rounded-lg py-1.5 px-2 flex items-center justify-center gap-1 top-5 left-5 border border-grayscale-600 text-lg ${status.color}`}>
          {status.icon && <IconSvg icon={status.icon as any} />}
          <span className="underline">{status.label}</span>
        </div>

        {imageUrl ? (
          <img src={imageUrl} alt={raffle.name ?? ''} className="object-cover w-full h-full" />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-grayscale-400 text-lg">
            {raffle.prizeType === 'RUNES' ? raffle.runesTokenName : 'No Image'}
          </div>
        )}

        <div className="absolute bottom-3 left-3 right-3 bg-[#10100FAD] rounded h-[36px] px-3 flex items-center gap-2 text-grayscale-300 text-sm font-semibold backdrop-blur-[6px]">
          <span>FP: {ticketPriceBtc} BTC</span>
          <span>|</span>
          <span>TTV: {totalValueBtc} BTC</span>
        </div>
      </div>

      <div className="p-5 space-y-4 rounded-lg bg-grayscale-800">
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <div className="flex-shrink-0 w-6 h-6 bg-white rounded-full"></div>
            <div className="font-medium text-brand-granite">You</div>
          </div>

          <div className="flex gap-2 items-center">
            <h4 className="text-grayscale-100">{raffle.name ?? 'Untitled Raffle'}</h4>
            <IconSvg icon="verifiedM" className="text-brand-tangerine" />
          </div>
        </div>

        <div className="flex justify-between">
          <div className="space-y-1">
            <div className="text-xs font-medium tracking-[-0.5px] text-brand-granite">Tickets Sold</div>
            <div className="text-lg font-semibold text-grayscale-100">
              {raffle.ticketsSold} / {raffle.supply.toLocaleString()}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs font-medium tracking-[-0.5px] text-brand-granite">End Date</div>
            <div className="text-lg font-semibold text-grayscale-100">{formattedEndDate}</div>
          </div>
        </div>

        {(raffle.status === 'DRAFT' || raffle.status === 'LIVE') && (
          <div onClick={e => e.stopPropagation()}>
            <CancelRaffleBtn
              raffleId={raffle.id}
              ticketsSold={raffle.ticketsSold}
              status={raffle.status}
              userId={userId}
              onCancelled={onCancelled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatedRaffleCard;
